import { PreloadedQuery, usePreloadedQuery } from 'react-relay'
import { HomeQuery } from './__generated__/HomeQuery.graphql'
import styled from '@emotion/styled'
import { Suspense } from 'react'
import { graphql } from 'relay-runtime'
import { useUserBrands } from '../hooks'
import { HomeUser$data } from './__generated__/HomeUser.graphql'
import CreativeList from '../features/creative/CreativeList'
import CreativeListSelector from '../features/creative/CreativeListSelector'
import { CreativeDrawerContainer } from '../features/creative/CreativeDrawer'
import HomeAction from '../features/creative/HomeAction'
import { SubmitCreativeForApprovalConfirmDialogContainer } from '../features/creative/SubmitCreativeForApprovalConfirmDialog'
import HomeLoading from './HomeLoading'
import { DeleteCommentConfirmDialogContainer } from '../features/comment/DeleteCommentConfirmDialog'
import { useGlobalState } from '../state/v2/store'
import { EmptyState } from '../components/EmptyState/EmptyState'
import { SelectableItemList } from '../components/SelectableItemList/SelectableItemList'
import { HomeListType } from '../state/url/type'

export const homeUserFragment = graphql`
  fragment HomeUser on User {
    accessibleBrands {
      id
      updatedAt
      metaAdAccounts {
        id
      }
    }
  }
`

export const homeQuery = graphql`
  query HomeQuery($input: GetRequest!, $metaAdsFilter: MetaAdsFilter) {
    getBrand(input: $input, metaAdsFilter: $metaAdsFilter) {
      id
      approvedMetaAdCount
      createdMetaAdCount
      draftMetaAdCount
      unapprovedMetaAdCount
      ...SubmitCreativeForApprovalConfirmDialogBrand
      ...CreativeDrawerBrand
      ...CreativeList
      ...HomeAction
    }
  }
`

interface HomeProps {
  homeQueryRef: PreloadedQuery<HomeQuery>
  refreshData: () => void
}

export const S = {
  Wrapper: styled.div`
    display: flex;
    flex-direction: row;
    height: 100%;
  `,
  SubHeader: styled.div`
    position: relative;
    display: flex;
    margin: 1rem;
    padding-top: 1rem;
  `,
  BrandSelectorWrapper: styled.div`
    width: 14rem;
    & > * {
      width: 14rem;
    }
  `,
  Selectors: styled.div`
    flex-grow: 1;
    display: flex;
    align-items: center;
    & > :not(:last-of-type) {
      margin-right: 1rem;
    }
  `,
  LoadingList: styled.div`
    margin: 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    & > * {
      margin-bottom: 1rem;
    }
  `,
  Body: styled.div`
    flex-grow: 1;
  `,
  Advisor: styled.div`
    height: 100%;
  `,
  EmptyState: styled(EmptyState)`
    margin-left: 12rem;
  `,
}

export const Home = ({ homeQueryRef, refreshData }: HomeProps) => {
  const homeData = usePreloadedQuery(homeQuery, homeQueryRef)
  const brands = useUserBrands<HomeUser$data>(homeUserFragment)
  const brandId = useGlobalState((b) => b.brand?.map((brand) => brand.id).join(","))

  if (brands.length === 0) {
    return (
      <S.EmptyState
        message="It looks like you don't have access to any brands.
        Please reach out to customer success in order to get access."
      />
    )
  }

  const selectedBrand = brands.filter((it) => it.id === brandId)
  const noMetaAdAccount =
    selectedBrand.length > 0 && selectedBrand[0].metaAdAccounts.length === 0

  const body = noMetaAdAccount ? (
    <S.EmptyState
      message="It looks like you don't have a Meta ad account connected to this brand.
        Please reach out to customer success in order to get set up."
    />
  ) : (
    <Suspense fallback={<HomeLoading />}>
      <CreativeList fragmentRequest={homeData.getBrand} />
    </Suspense>
  )

  return (
    <SelectableItemList>
      <CreativeDrawerContainer brandFragmentRequest={homeData.getBrand} />
      <DeleteCommentConfirmDialogContainer refreshData={refreshData} />
      <SubmitCreativeForApprovalConfirmDialogContainer
        open={false}
        brandFragmentRequest={homeData.getBrand}
        refreshData={refreshData}
      />
      <S.Wrapper className="px-8">
        <S.Body>
          <S.SubHeader>
            <S.Selectors>
              <CreativeListSelector
                type={HomeListType.DRAFT}
                count={homeData.getBrand.draftMetaAdCount}
              />
              <CreativeListSelector
                type={HomeListType.UNAPPROVED}
                count={homeData.getBrand.unapprovedMetaAdCount}
              />
              <CreativeListSelector
                type={HomeListType.APPROVED}
                count={homeData.getBrand.approvedMetaAdCount}
              />
              <CreativeListSelector
                type={HomeListType.CREATED}
                count={homeData.getBrand.createdMetaAdCount}
              />
            </S.Selectors>
            <HomeAction fragmentRequest={homeData.getBrand} />
            <div />
          </S.SubHeader>
          {body}
        </S.Body>
      </S.Wrapper>
    </SelectableItemList>
  )
}

export default Home
